import { Component, OnInit, ViewChild } from "@angular/core";
import { NewDialogComponent } from "common/newdialog/dialog.component";
import { MutexService } from "common/concurrency/mutex.service";
import { Observable } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import {
	DependentElement,
	DependentElementsManagementService,
} from "process/dependant-elements/dependent-elements-management.service";
import { ViewService } from "core/view.service";
import { TranslateChoiceService } from "core/translate/translate-choice.service";
import ProcessView = stages.process.ProcessView;
import ViewableDependentElementTemplate = stages.process.ViewableDependentElementTemplate;

interface DialogElement {
	label: string;
	id: string;
	class: StringToBoolean;
	sameLabelExists: boolean;
}

@Component({
	selector: "stages-dependent-element-confirm-dialog",
	templateUrl: "dependent-element-confirm-dialog.component.html",
})
export class DependentElementConfirmDialogComponent implements OnInit {
	processView$: Observable<ProcessView>;
	dependentElements: DependentElement[] = [];
	dialogElements: DialogElement[] = [];
	dependentElementIdsWithoutDuplicates: string[] = [];

	private parentId!: string;
	private parentTypeIdent!: string;
	private dependentElementTypeIdent!: string;
	private templateId!: string;
	countMessageKey?: string;
	applyInProgress: boolean = false;

	@ViewChild("dialog")
	dialog!: NewDialogComponent;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly dependentElementsManagementService: DependentElementsManagementService,
		private readonly mutexService: MutexService,
		private readonly viewService: ViewService,
		private readonly translateChoiceService: TranslateChoiceService,
	) {
		this.processView$ = this.viewService.awaitSelfElementObservable();
	}

	ngOnInit(): void {
		this.templateId = this.route.snapshot.params.templateId;
		this.dependentElementTypeIdent = this.route.snapshot.params.dependentElementTypeIdent;

		this.processView$.subscribe((processView: ProcessView) => {
			this.parentId = processView.id;
			this.parentTypeIdent = processView.type.ident;

			this.dependentElements =
				processView.dependentElements && this.dependentElementTypeIdent
					? processView.dependentElements.filter(
							(dependentElement) => dependentElement.type.subtypeIdent === this.dependentElementTypeIdent,
					  )
					: [];

			const templateData = processView.applicableDependentElementTemplates?.find(
				(template) => template.id === this.templateId,
			);
			if (templateData === undefined) {
				return;
			}
			this.initializeDialogElements(templateData);
		});

		this.dependentElementIdsWithoutDuplicates = this.dialogElements
			.filter((element) => !element.sameLabelExists)
			.map((element) => element.id);

		this.countMessageKey = this.getMessageKey();
	}

	private initializeDialogElements(templateData: ViewableDependentElementTemplate): void {
		templateData.children!.forEach((element) =>
			this.dialogElements.push({
				label: element.label,
				id: element.id,
				class: { tailored: element.tailored },
				sameLabelExists: this.dependentElementsManagementService.elementWithSameLabelExists(
					element.label,
					this.dependentElements.map((dependentElement) => dependentElement.label),
				),
			}),
		);
	}

	private getMessageKey(): string {
		const templateItemCount = this.dependentElementIdsWithoutDuplicates.length;
		const elementTypeLabel = this.translateChoiceService.getElementTypeTranslation(
			templateItemCount,
			this.parentTypeIdent,
			this.dependentElementTypeIdent,
		);
		return this.translateChoiceService.getTranslation(
			{ count: templateItemCount, elementType: elementTypeLabel },
			"process.element.dependentElementsList.confirmTemplate.{count}",
		);
	}

	applyTemplate(): void {
		this.applyInProgress = true;
		const paramMap = this.route.snapshot.paramMap;
		this.mutexService.invoke("applyDependentElementTemplate", async () => {
			await this.dependentElementsManagementService
				.applyDependentElementTemplate(
					paramMap.get("workspaceId")!,
					this.parentTypeIdent,
					this.parentId,
					this.dependentElementTypeIdent,
					this.templateId,
					this.dependentElementIdsWithoutDuplicates,
				)
				.then(() => {
					this.dialog.close();
					this.viewService.refresh(paramMap);
					this.applyInProgress = false;
				});
		});
	}

	close(): void {
		this.dialog.close();
	}
}
